import { Box, List, ListItemText, Typography } from '@mui/material'
import { EmptyList } from 'components/core/empty-list/'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1AccessReviewDisplayName } from 'components/pages/accessreview/display-name'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { C1PolicyDisplayName } from 'components/pages/policy/display-name'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useLocalStorage } from 'hooks/local-storage/index'
import { AccessReviewValue } from 'lib/actions/access_review'
import { AppValue } from 'lib/actions/apps'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { UserValue } from 'lib/actions/user'
import { routes } from 'lib/routes'
import { ChevronRight } from 'lucide-react'

import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { FC, useRef, useEffect, useMemo, useCallback, MutableRefObject } from 'react'
import { FormattedMessage } from 'react-intl'
import { getUpdatedRecentSearch, RecentSearches, SearchableSpotlightItem, SearchResult, SPOTLIGHT_SEARCH_STORAGE_KEY, useSearchableSpotlightItems } from './RecentSearches'
import { SectionHeader } from './SectionHeader'
import { SpotlightResultItem } from './SpotlightResultItem'

/** Spotlight item for pages */
const SpotlightItem: FC<{
    item: SearchableSpotlightItem
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <ListItemText>
            <Typography sx={{ display: 'flex', alignItems: 'center' }} noWrap>
                <C1Icon icon={item.icon} size="sm" sx={{ mr: 1 }} />
                {item.section && (
                    <>
                        <Typography component="span" color="text.secondary">{item.section}</Typography>
                        <C1Icon icon={ChevronRight} size="sm" sx={{ color: 'text.secondary', mx: 1 }} />
                    </>
                )}
                <Typography component="span">{item.text}</Typography>
            </Typography>
        </ListItemText>
    </SpotlightResultItem>

/** User result item using UserDisplayName */
const UserItem: FC<{
    item: UserValue
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <UserDisplayName user={item} readOnly margin={false} />
    </SpotlightResultItem>

/** Entitlement result item using C1EntitlementDisplayName */
const EntitlementItem: FC<{
    item: AppEntitlementValue
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <C1EntitlementDisplayName entitlement={item} readOnly />
    </SpotlightResultItem>

/** App result item using AppDisplayName */
const AppItem: FC<{
    item: AppValue
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <AppDisplayName app={item} readOnly avatarSize="md" margin={false} />
    </SpotlightResultItem>

/** Campaign result item using C1AccessReviewDisplayName */
const CampaignItem: FC<{
    item: AccessReviewValue
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <C1AccessReviewDisplayName accessReview={item} readOnly />
    </SpotlightResultItem>

/** Policy result item using C1PolicyDisplayName */
const PolicyItem: FC<{
    item: Policy
    onClick: () => void
    selected?: boolean
}> = ({ item, onClick, selected }) =>
    <SpotlightResultItem onClick={onClick} selected={selected}>
        <C1PolicyDisplayName policy={item} readOnly />
    </SpotlightResultItem>

type ResultsProps = {
    entitlements: readonly AppEntitlementValue[]
    users: readonly UserValue[]
    campaigns: readonly AccessReviewValue[]
    apps: readonly AppValue[]
    policies: readonly Policy[]
    pages: readonly SearchableSpotlightItem[]
    goTo: (path: string) => void
    loadingErrorText: string
    selectedIndex: number
    totalResultsRef: MutableRefObject<number>
    activateSelectedRef: MutableRefObject<(() => void) | null>
}

const Results: FC<ResultsProps> = ({
    entitlements, users, campaigns, apps, policies, pages,
    goTo, loadingErrorText, selectedIndex, totalResultsRef, activateSelectedRef,
}) => {
    const [recentSearches, saveRecentSearch] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])

    const listRef = useRef<HTMLElement>(null)

    // Helper to save to recents and navigate
    const saveAndGoTo = useCallback((result: SearchResult, path: string): void => {
        saveRecentSearch(getUpdatedRecentSearch(result, recentSearches))
        goTo(path)
    }, [recentSearches, saveRecentSearch, goTo])

    // Build flat list for keyboard navigation with SearchResult data and paths
    const allItems = useMemo(() => {
        const items: Array<{ id: string, result: SearchResult, path: string }> = []

        // Pages (max 3) - store spotlightId to look up at render time
        pages.slice(0, 3).forEach((item) => {
            items.push({
                id: item.id,
                path: item.pathname,
                result: {
                    category: 'page',
                    spotlightId: item.id,
                },
            })
        })

        // Apps (max 3) - store AppRef
        apps?.slice(0, 3).forEach((item) => {
            items.push({
                id: `app-${item.id}`,
                path: `/admin/applications/${item.id}`,
                result: {
                    category: 'app',
                    ref: { id: item.id },
                },
            })
        })

        // Entitlements (max 3) - store AppEntitlementRef
        entitlements?.slice(0, 3).forEach((item) => {
            items.push({
                id: `entitlement-${item.id}`,
                path: `/admin/applications/${item.appId}/entitlements/${item.id}`,
                result: {
                    category: 'entitlement',
                    ref: { id: item.id, appId: item.appId },
                },
            })
        })

        // Users (max 3) - store UserRef
        users?.slice(0, 3).forEach((item) => {
            items.push({
                id: `user-${item.id}`,
                path: `/admin/directory/users/${item.id}`,
                result: {
                    category: 'user',
                    ref: { id: item.id },
                },
            })
        })

        // Policies (max 3) - store PolicyRef
        policies?.slice(0, 3).forEach((item) => {
            items.push({
                id: `policy-${item.id}`,
                path: routes.settings.policies.detail(item.id),
                result: {
                    category: 'policy',
                    ref: { id: item.id },
                },
            })
        })

        // Campaigns (max 3) - store AccessReviewRef
        campaigns?.slice(0, 3).forEach((item) => {
            items.push({
                id: `campaign-${item.id}`,
                path: `/admin/campaigns/${item.id}`,
                result: {
                    category: 'campaign',
                    ref: { id: item.id },
                },
            })
        })

        return items
    }, [pages, users, entitlements, apps, policies, campaigns])

    // Update the total results ref
    useEffect(() => {
        totalResultsRef.current = allItems.length
    }, [allItems.length, totalResultsRef])

    // Update the activate selected ref
    useEffect(() => {
        activateSelectedRef.current = (): void => {
            if (selectedIndex >= 0 && selectedIndex < allItems.length) {
                const item = allItems[selectedIndex]
                saveAndGoTo(item.result, item.path)
            }
        }
    }, [selectedIndex, allItems, saveAndGoTo, activateSelectedRef])

    // Scroll selected item into view
    useEffect(() => {
        const list = listRef.current
        if (!list) return

        const selectedButton = list.querySelector('[class*="Mui-selected"]') as HTMLElement
        if (selectedButton) {
            selectedButton.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
    }, [selectedIndex])

    // Calculate indices for selection
    let currentIndex = 0
    const pagesStartIndex = currentIndex
    currentIndex += Math.min(pages.length, 3)
    const appsStartIndex = currentIndex
    currentIndex += Math.min(apps?.length ?? 0, 3)
    const entitlementsStartIndex = currentIndex
    currentIndex += Math.min(entitlements?.length ?? 0, 3)
    const usersStartIndex = currentIndex
    currentIndex += Math.min(users?.length ?? 0, 3)
    const policiesStartIndex = currentIndex
    currentIndex += Math.min(policies?.length ?? 0, 3)
    const campaignsStartIndex = currentIndex

    const hasPages = pages.length > 0
    const hasUsers = users && users.length > 0
    const hasEntitlements = entitlements && entitlements.length > 0
    const hasApps = apps && apps.length > 0
    const hasPolicies = policies && policies.length > 0
    const hasCampaigns = campaigns && campaigns.length > 0

    return (
        <Box ref={listRef} pt={1}>
            {hasPages && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_pages" defaultMessage="Pages" />
                    </SectionHeader>
                    <List disablePadding>
                        {pages.slice(0, 3).map((item, idx) => (
                            <SpotlightItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'page',
                                    spotlightId: item.id,
                                }, item.pathname)}
                                selected={pagesStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {hasApps && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_apps" defaultMessage="Apps" />
                    </SectionHeader>
                    <List disablePadding>
                        {apps.slice(0, 3).map((item, idx) => (
                            <AppItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'app',
                                    ref: { id: item.id },
                                }, `/admin/applications/${item.id}`)}
                                selected={appsStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {hasEntitlements && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_entitlements" defaultMessage="Entitlements" />
                    </SectionHeader>
                    <List disablePadding>
                        {entitlements.slice(0, 3).map((item, idx) => (
                            <EntitlementItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'entitlement',
                                    ref: { id: item.id, appId: item.appId },
                                }, `/admin/applications/${item.appId}/entitlements/${item.id}`)}
                                selected={entitlementsStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {hasUsers && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_users" defaultMessage="Users" />
                    </SectionHeader>
                    <List disablePadding>
                        {users.slice(0, 3).map((item, idx) => (
                            <UserItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'user',
                                    ref: { id: item.id },
                                }, `/admin/directory/users/${item.id}`)}
                                selected={usersStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {hasPolicies && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_policies" defaultMessage="Policies" />
                    </SectionHeader>
                    <List disablePadding>
                        {policies.slice(0, 3).map((item, idx) => (
                            <PolicyItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'policy',
                                    ref: { id: item.id },
                                }, routes.settings.policies.detail(item.id))}
                                selected={policiesStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {hasCampaigns && (
                <>
                    <SectionHeader>
                        <FormattedMessage id="spotlight_campaigns" defaultMessage="Campaigns" />
                    </SectionHeader>
                    <List disablePadding>
                        {campaigns.slice(0, 3).map((item, idx) => (
                            <CampaignItem
                                key={item.id}
                                item={item}
                                onClick={(): void => saveAndGoTo({
                                    category: 'campaign',
                                    ref: { id: item.id },
                                }, `/admin/campaigns/${item.id}`)}
                                selected={campaignsStartIndex + idx === selectedIndex}
                            />
                        ))}
                    </List>
                </>
            )}

            {loadingErrorText && <Box pt={3}><MinimalAPIError err={<Typography>{loadingErrorText}</Typography>} /></Box>}
        </Box>
    )
}

type ListSearchResultsProps = {
    entitlements: readonly AppEntitlementValue[]
    users: readonly UserValue[]
    campaigns: readonly AccessReviewValue[]
    apps: readonly AppValue[]
    policies: readonly Policy[]
    isLoading: boolean
    isEmpty: boolean
    goTo: (path: string) => void
    loadingErrorText: string
    searchQuery: string
    selectedIndex: number
    totalResultsRef: MutableRefObject<number>
    activateSelectedRef: MutableRefObject<(() => void) | null>
}

export const ListSearchResults: FC<ListSearchResultsProps> = ({
    entitlements,
    users,
    campaigns,
    apps,
    policies,
    isLoading: _isLoading,
    isEmpty,
    goTo,
    loadingErrorText,
    searchQuery,
    selectedIndex,
    totalResultsRef,
    activateSelectedRef,
}) => {
    const spotlightItems = useSearchableSpotlightItems()

    // Filter spotlight items (pages) based on search query
    const filteredPages = useMemo(() => {
        if (!searchQuery) return []

        const query = searchQuery.toLowerCase()
        const pages: SearchableSpotlightItem[] = []

        for (const item of spotlightItems) {
            // Match against searchText and sectionSearchText
            const matches = item.searchText.includes(query) ||
                (item.sectionSearchText && item.sectionSearchText.includes(query))

            if (matches) {
                pages.push(item)
            }
        }

        return pages
    }, [spotlightItems, searchQuery])

    if (isEmpty) {
        return <RecentSearches
            goTo={goTo}
            selectedIndex={selectedIndex}
            totalResultsRef={totalResultsRef}
            activateSelectedRef={activateSelectedRef}
        />
    }

    const hasUsers = users && users.length > 0
    const hasEntitlements = entitlements && entitlements.length > 0
    const hasCampaigns = campaigns && campaigns.length > 0
    const hasApps = apps && apps.length > 0
    const hasPolicies = policies && policies.length > 0
    const hasPages = filteredPages.length > 0
    const hasAnyApiResults = hasUsers || hasEntitlements || hasCampaigns || hasApps || hasPolicies
    const hasAnySpotlightResults = hasPages

    // Show empty state if no results (even while loading, to prevent layout jumping)
    if (!hasAnyApiResults && !hasAnySpotlightResults) {
        return <Box pt={3}><EmptyList text={<FormattedMessage id="no_search_results" defaultMessage="No results found." />} /></Box>
    }

    return <Results
        entitlements={entitlements}
        users={users}
        campaigns={campaigns}
        apps={apps}
        policies={policies}
        pages={filteredPages}
        goTo={goTo}
        loadingErrorText={loadingErrorText}
        selectedIndex={selectedIndex}
        totalResultsRef={totalResultsRef}
        activateSelectedRef={activateSelectedRef}
    />
}
